import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { useReveal } from "../hooks/useReveal";
import Header from "../components/Layout/Header";
import Footer from "../components/Layout/Footer";
import { Section, Panel, Eyebrow, Lead, CenteredHead } from "../components/ui";
import Faq from "../sections/Faq";
import FinalCta from "../sections/FinalCta";
import { sendContact, honeypotStyle } from "../utils/contactApi";

const SUPPORT_EMAIL = "support@nea.lu";

const SUBJECTS = [
  { value: "app", label: "Question sur l'app" },
  { value: "bug", label: "Problème technique" },
  { value: "bank", label: "Connexion bancaire" },
  { value: "security", label: "Sécurité et données" },
  { value: "feature", label: "Suggestion de fonctionnalité" },
  { value: "other", label: "Autre" },
];

const Wrapper = styled.div`
  width: 100%;
  overflow-x: hidden;
  background-color: var(--paper);
  color: var(--text-hi);
`;

const TopSection = styled(Section)`
  padding-top: 6.5rem;

  @media screen and (max-width: 48em) {
    padding-top: 5.5rem;
  }
`;

const Title = styled.h1`
  font-family: var(--fontL);
  font-weight: 500;
  font-size: clamp(2.25rem, 5vw, 3.75rem);
  line-height: 1.08;
  letter-spacing: -0.03em;
  margin-bottom: 1.25rem;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 1.5rem;
  align-items: start;
  max-width: 1040px;
  margin: 0 auto;

  @media screen and (max-width: 56em) {
    grid-template-columns: 1fr;
  }
`;

const FormCard = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding: 2.25rem;
  border-radius: 24px;
  background: #ffffff;
  font-family: var(--fontR);

  @media screen and (max-width: 48em) {
    padding: 1.5rem;
  }
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media screen and (max-width: 30em) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;

  label {
    font-size: 0.875rem;
    font-weight: 500;
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 0.8125rem 1rem;
    border-radius: 12px;
    border: 1px solid var(--line);
    background: var(--paper);
    color: var(--text-hi);
    font-family: var(--fontR);
    font-size: 1rem;
  }

  textarea {
    resize: vertical;
    min-height: 8rem;
  }

  input::placeholder,
  textarea::placeholder {
    color: #9a9aa0;
  }

  input:focus-visible,
  select:focus-visible,
  textarea:focus-visible {
    outline: 2px solid var(--green);
    outline-offset: 1px;
    border-color: transparent;
  }

  input[aria-invalid="true"],
  select[aria-invalid="true"],
  textarea[aria-invalid="true"] {
    border-color: #b3412e;
  }
`;

const FieldError = styled.p`
  font-size: 0.8125rem;
  color: #b3412e;
`;

const Submit = styled.button`
  align-self: flex-start;
  height: 52px;
  padding: 0 1.75rem;
  border: none;
  border-radius: var(--radius-full);
  background: var(--ink-900);
  color: var(--text-inv);
  font-family: var(--fontR);
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 150ms ease, transform 150ms ease;

  &:hover {
    background: var(--ink-700);
    transform: translateY(-1px);
  }

  @media screen and (max-width: 30em) {
    align-self: stretch;
  }
`;

const Notice = styled.p`
  padding: 1rem 1.125rem;
  border-radius: 12px;
  background: rgba(21, 184, 127, 0.12);
  color: #0a5c40;
  font-size: 0.9375rem;
  line-height: 1.5;

  a {
    color: inherit;
  }
`;

const Aside = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const InfoCard = styled.div`
  padding: 1.75rem 1.5rem;
  border-radius: 20px;
  background: #ffffff;
  font-family: var(--fontR);

  h2 {
    font-family: var(--fontL);
    font-size: 1.125rem;
    font-weight: 500;
    letter-spacing: -0.01em;
    margin-bottom: 0.5rem;
  }

  p {
    font-size: 0.9375rem;
    line-height: 1.55;
    color: var(--text-lo);
  }

  a {
    color: var(--text-hi);
    font-weight: 500;
    text-underline-offset: 3px;
  }
`;

const EMPTY_FORM = { name: "", email: "", subject: "", message: "", website: "" };

const Support = () => {
  const revealRef = useReveal();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  // null | "pending" | "sent" | "throttled" | "mailto"
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  const validate = () => {
    const next = {};
    if (!formData.name.trim()) next.name = "Ton nom est requis.";
    if (!formData.email.trim()) next.email = "Ton email est requis.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) next.email = "Cet email ne semble pas valide.";
    if (!formData.subject) next.subject = "Choisis un sujet.";
    if (formData.message.trim().length < 10) next.message = "Dis-nous en un peu plus (10 caractères minimum).";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
    if (errors[name]) setErrors((previous) => ({ ...previous, [name]: "" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setOutcome(null);
    if (!validate()) return;

    const topic = SUBJECTS.find((item) => item.value === formData.subject)?.label || formData.subject;
    setOutcome("pending");
    const result = await sendContact({
      type: "support",
      name: formData.name.trim(),
      email: formData.email.trim(),
      subject: topic,
      message: formData.message.trim(),
      website: formData.website,
    });
    if (result === "sent") {
      setFormData(EMPTY_FORM);
      setOutcome("sent");
      return;
    }
    if (result === "throttled") {
      setOutcome("throttled");
      return;
    }

    // The API could not take it: hand the message over to the visitor's mail app rather than pretending it was sent
    const subject = encodeURIComponent(`[Support nea] ${topic}`);
    const body = encodeURIComponent(`${formData.message}\n\n${formData.name}\n${formData.email}`);
    window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
    setOutcome("mailto");
  };

  return (
    <Wrapper ref={revealRef}>
      <Header />
      <main>
        <TopSection>
          <Panel>
            <CenteredHead>
              <Eyebrow $color="#0a7a53">Support</Eyebrow>
              <Title>Besoin d'aide ?</Title>
              <Lead style={{ maxWidth: "34rem" }}>
                Une question, un bug, une idée pour améliorer l'app ? Écris-nous, on lit tout et on te
                répond.
              </Lead>
            </CenteredHead>

            <Grid>
              <FormCard onSubmit={handleSubmit} noValidate>
                <FieldRow>
                  <Field>
                    <label htmlFor="name">Nom</label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      autoComplete="name"
                      placeholder="Ton nom"
                      value={formData.name}
                      onChange={handleChange}
                      aria-invalid={Boolean(errors.name)}
                    />
                    {errors.name && <FieldError>{errors.name}</FieldError>}
                  </Field>
                  <Field>
                    <label htmlFor="email">Email</label>
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      placeholder="ton@email.com"
                      value={formData.email}
                      onChange={handleChange}
                      aria-invalid={Boolean(errors.email)}
                    />
                    {errors.email && <FieldError>{errors.email}</FieldError>}
                  </Field>
                </FieldRow>

                <Field>
                  <label htmlFor="subject">Sujet</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.subject)}
                  >
                    <option value="">Choisis un sujet</option>
                    {SUBJECTS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  {errors.subject && <FieldError>{errors.subject}</FieldError>}
                </Field>

                <Field>
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    placeholder="Décris ta question ou ton problème…"
                    value={formData.message}
                    onChange={handleChange}
                    aria-invalid={Boolean(errors.message)}
                  />
                  {errors.message && <FieldError>{errors.message}</FieldError>}
                </Field>

                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  style={honeypotStyle}
                  value={formData.website}
                  onChange={handleChange}
                />

                <Submit type="submit" disabled={outcome === "pending"}>
                  {outcome === "pending" ? "Envoi…" : "Envoyer le message"}
                </Submit>

                {outcome === "sent" && (
                  <Notice role="status">
                    <strong>Message envoyé.</strong> On te répond au plus vite, à l'adresse que tu as indiquée.
                  </Notice>
                )}
                {outcome === "throttled" && (
                  <Notice role="status">
                    <strong>Trop de messages d'un coup.</strong> Réessaie dans une minute, ou écris-nous à{" "}
                    <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                  </Notice>
                )}
                {outcome === "mailto" && (
                  <Notice role="status">
                    <strong>Ton app mail vient de s'ouvrir.</strong> Envoie le message prérempli et on te répond au
                    plus vite. Rien ne s'ouvre ? Écris-nous directement à{" "}
                    <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                  </Notice>
                )}
              </FormCard>

              <Aside>
                <InfoCard>
                  <h2>Par email, directement</h2>
                  <p>
                    Tu préfères ton propre mail ? Écris à <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                  </p>
                </InfoCard>
                <InfoCard>
                  <h2>Tes données et ta sécurité</h2>
                  <p>
                    Tout est détaillé dans notre <Link to="/privacy">politique de confidentialité</Link>. Pour exercer
                    tes droits, écris à <a href="mailto:info@nea.lu">info@nea.lu</a>.
                  </p>
                </InfoCard>
                <InfoCard>
                  <h2>Un souci de connexion bancaire ?</h2>
                  <p>
                    Indique-nous le nom de ta banque et ce qui s'affiche à l'écran : c'est ce qui nous aide le plus à
                    débloquer la situation.
                  </p>
                </InfoCard>
              </Aside>
            </Grid>
          </Panel>
        </TopSection>

        <Faq />
        <FinalCta />
      </main>
      <Footer />
    </Wrapper>
  );
};

export default Support;
